import { z } from 'zod';
import { auditSchema, hexColorSchema, idSchema, isoDateTimeSchema } from '../../common.js';
/** Yjs doc holding `Y.Map<eventId, CalendarEvent>` (ROADMAP §1.3). */
export const CALENDAR_DOC = 'calendar';
/** Key of the `Y.Map<eventId, CalendarEvent>` inside the `calendar` doc. */
export const CALENDAR_EVENTS_KEY = 'events';
/**
 * Keys the calendar *reads* inside the `household` doc — birthdays surface in calendar
 * views and member profiles drive colour coding (DESIGN-BRIEF §4 screen 9, ROADMAP §1.3).
 * They live here rather than in the household module because WS-4 is the first consumer;
 * whoever builds the household UI should import these instead of restating the strings.
 */
export const HOUSEHOLD_MEMBERS_KEY = 'members';
export const HOUSEHOLD_BIRTHDAYS_KEY = 'birthdays';
export const reminderSchema = z.object({
    /** Minutes before `start` that the reminder fires. */
    minutesBefore: z
        .number()
        .int()
        .min(0)
        .max(60 * 24 * 30),
});
/** Reminder offsets offered in the event form (ROADMAP §2 Phase 1 — ≤3 per event). */
export const REMINDER_PRESETS = [
    { minutesBefore: 0, label: 'At start' },
    { minutesBefore: 10, label: '10 min before' },
    { minutesBefore: 30, label: '30 min before' },
    { minutesBefore: 60, label: '1 hour before' },
    { minutesBefore: 120, label: '2 hours before' },
    { minutesBefore: 60 * 24, label: '1 day before' },
    { minutesBefore: 60 * 24 * 2, label: '2 days before' },
];
export const MAX_REMINDERS = 3;
/**
 * Where an event came from. Nothing in core may assume events originate in AwwYeah:
 * Phase 4 (calendar bridge) surfaces read-only external events (ROADMAP §2 Phase 4).
 */
export const eventSourceSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('local') }),
    z.object({
        kind: z.literal('external'),
        provider: z.enum(['google', 'caldav', 'ics']),
        calendarId: z.string().min(1),
        externalId: z.string().min(1),
        /** External events are never editable in AwwYeah. */
        readOnly: z.literal(true),
    }),
]);
export const LOCAL_EVENT_SOURCE = { kind: 'local' };
export const calendarEventSchema = auditSchema.extend({
    id: idSchema,
    title: z.string().min(1).max(500),
    start: isoDateTimeSchema,
    end: isoDateTimeSchema,
    allDay: z.boolean(),
    /** Household members the event belongs to; drives colour coding and filters. */
    ownerIds: z.array(idSchema).max(16),
    color: hexColorSchema.optional(),
    location: z.string().max(500).optional(),
    notes: z.string().max(10_000).optional(),
    reminders: z.array(reminderSchema).max(MAX_REMINDERS),
    /** RFC 5545 RRULE string, e.g. `FREQ=WEEKLY;BYDAY=MO`. */
    recurrence: z.string().max(500).optional(),
    /**
     * Occurrence starts removed from `recurrence` — the "exceptions" half of
     * "recurring events are stored as rule + exceptions" (ROADMAP §2 Phase 1). An
     * occurrence edited on its own gets an exdate here plus a detached event carrying
     * `recurrenceParentId`/`recurrenceId`.
     */
    exdates: z.array(isoDateTimeSchema).max(500).default([]),
    /** Set on a detached occurrence: the recurring event it was split out of. */
    recurrenceParentId: idSchema.optional(),
    /** Set on a detached occurrence: the original occurrence start it replaces. */
    recurrenceId: isoDateTimeSchema.optional(),
    source: eventSourceSchema.default({ kind: 'local' }),
    /**
     * **Soft** subset sharing (WS-15). Absent = everyone, which is what every event written
     * before WS-15 means. When present, only these members (plus the creator and any owner)
     * are *shown* the event.
     *
     * Be honest about what this is: the event still lives in the shared `calendar` doc, so
     * anybody who can open that doc can read it with a debugger. It is a "don't clutter
     * everyone's calendar" control, not a secret. "Just me" is the hard one — it writes into
     * `calendar:user:<id>` (`personalCalendarDocName`), which the sync hub enforces.
     */
    audience: z.array(idSchema).max(64).optional(),
    /**
     * Set by importers that may run again over the same span (the work-schedule photo
     * import, 2026-09-08): `work:<memberId>:<date>`. A re-import replaces events carrying a
     * matching key instead of duplicating them. Absent on hand-made events.
     */
    importKey: z.string().max(200).optional(),
});
/**
 * Should `userId` see this event? Absent `audience` = everyone. The creator and the
 * event's owners always see it — otherwise assigning an event to someone and then
 * narrowing the audience would hide it from the person it is about.
 */
export function isEventVisibleTo(event, userId) {
    if (!event.audience)
        return true;
    if (userId === null)
        return false;
    if (event.createdBy === userId)
        return true;
    if (event.ownerIds.includes(userId))
        return true;
    return event.audience.includes(userId);
}
/** UI guard: external events are read-only everywhere in core. */
export function isEventEditable(event) {
    return event.source.kind === 'local';
}
/**
 * Parses a value out of the Yjs map. Docs are collaborative and long-lived, so a value
 * written by an older/newer client must never crash a render — callers drop `null`s.
 */
export function parseCalendarEvent(value) {
    const parsed = calendarEventSchema.safeParse(value);
    return parsed.success ? parsed.data : null;
}
//# sourceMappingURL=schema.js.map